import { useEffect, useState } from 'react';

/**
 * Hash routing, which is not just a shortcut here.
 *
 * Two of these routes carry a secret — an invite token and a frame's public
 * slug. A fragment is never sent to the server, so neither one appears in a
 * Cloudflare access log, and the client posts them in a request body instead.
 * Path-based routing would leak both by construction.
 */

export type Route =
  | { readonly kind: 'devices' }
  | { readonly kind: 'device'; readonly id: string; readonly tab: string }
  | { readonly kind: 'albums' }
  | { readonly kind: 'album'; readonly id: string }
  | { readonly kind: 'join'; readonly invite: string }
  | { readonly kind: 'frame'; readonly slug: string }
  | { readonly kind: 'signin' }
  | { readonly kind: 'signup' }
  | { readonly kind: 'account' }
  /** `checkout` is set on the way back from Stripe, and only then. */
  | { readonly kind: 'billing'; readonly checkout: 'success' | null };

export const parseRoute = (hash: string): Route => {
  const [rawPath = '', rawQuery = ''] = hash.replace(/^#/, '').split('?');
  const segments = rawPath.split('/').filter(Boolean);
  const query = new URLSearchParams(rawQuery);

  const [head, second, third] = segments;

  if (head === 'join') return { kind: 'join', invite: query.get('i') ?? '' };
  if (head === 'f' && second) return { kind: 'frame', slug: second };
  if (head === 'd' && second) return { kind: 'device', id: second, tab: third ?? 'photos' };
  if (head === 'a' && second) return { kind: 'album', id: second };
  if (head === 'a') return { kind: 'albums' };
  if (head === 'signin') return { kind: 'signin' };
  if (head === 'signup') return { kind: 'signup' };
  if (head === 'account') return { kind: 'account' };
  if (head === 'billing') {
    return { kind: 'billing', checkout: query.get('checkout') === 'success' ? 'success' : null };
  }
  return { kind: 'devices' };
};

export const useRoute = (): Route => {
  const [route, setRoute] = useState<Route>(() => parseRoute(window.location.hash));

  useEffect(() => {
    const onChange = () => setRoute(parseRoute(window.location.hash));
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);

  return route;
};

export const go = (path: string): void => {
  window.location.hash = path;
};

/** Drops a secret out of the address bar once it has been used. */
export const replaceHash = (path: string): void => {
  window.history.replaceState(null, '', `${window.location.pathname}#${path}`);
  window.dispatchEvent(new HashChangeEvent('hashchange'));
};
